<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-content">
      <div class="error-icon">⚠️</div>
      <h2 class="error-title">Something went wrong</h2>
      <p class="error-message">
        The page failed to load properly. This might be due to a temporary issue.
      </p>
      <div class="error-actions">
        <button @click="retry" class="btn btn-primary">
          <span class="retry-icon">🔄</span>
          Try Again
        </button>
        <button @click="goHome" class="btn btn-secondary">
          <span class="home-icon">🏠</span>
          Go Home
        </button>
      </div>
      <details v-if="showDetails" class="error-details">
        <summary>Technical Details</summary>
        <pre class="error-stack">{{ errorInfo }}</pre>
      </details>
      <button 
        v-if="!showDetails" 
        @click="showDetails = true" 
        class="btn-link"
      >
        Show Technical Details
      </button>
    </div>
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
import { ref, onErrorCaptured, provide, inject } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const hasError = ref(false)
const errorInfo = ref('')
const showDetails = ref(false)

// Provide error handling to child components
provide('errorBoundary', {
  reportError: (error: Error) => {
    hasError.value = true
    errorInfo.value = error.stack || error.message
    console.error('Error boundary caught:', error)
  }
})

// Inject error reporting for child components
const reportError = inject<(error: Error) => void>('reportError', () => {})

// Capture Vue component errors
onErrorCaptured((error: Error, instance: any, info: string) => {
  hasError.value = true
  errorInfo.value = `${error.message}\n\nComponent: ${instance?.$options?.name || 'Unknown'}\nInfo: ${info}\nStack: ${error.stack}`
  console.error('Vue error captured:', error, info)
  
  // Report to parent error boundary if available
  if (reportError) {
    reportError(error)
  }
  
  return false // Prevent error from propagating further
})

// Global error handler for unhandled errors
window.addEventListener('error', (event) => {
  hasError.value = true
  errorInfo.value = `${event.error?.message || event.message}\n\nFile: ${event.filename}\nLine: ${event.lineno}\nColumn: ${event.colno}\nStack: ${event.error?.stack}`
  console.error('Global error caught:', event.error)
})

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  hasError.value = true
  errorInfo.value = `Unhandled Promise Rejection: ${event.reason}\n\nStack: ${event.reason?.stack || 'No stack trace available'}`
  console.error('Unhandled promise rejection:', event.reason)
})

const retry = () => {
  hasError.value = false
  errorInfo.value = ''
  showDetails.value = false
  
  // Force a re-render by navigating to the same route
  const currentRoute = router.currentRoute.value
  router.replace({ path: currentRoute.path, query: { ...currentRoute.query, _retry: Date.now() } })
}

const goHome = () => {
  hasError.value = false
  errorInfo.value = ''
  showDetails.value = false
  router.push('/')
}
</script>

<style scoped>
.error-boundary {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--background-color);
}

.error-content {
  max-width: 500px;
  text-align: center;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--box-shadow);
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-title {
  color: var(--text-color);
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.error-message {
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
}

.error-actions .btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.retry-icon,
.home-icon {
  font-size: 1.1rem;
}

.error-details {
  text-align: left;
  margin-top: 1rem;
}

.error-details summary {
  cursor: pointer;
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.error-stack {
  background: var(--input-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--text-color);
  white-space: pre-wrap;
  word-break: break-word;
}

.btn-link {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.9rem;
}

.btn-link:hover {
  color: var(--primary-hover);
}

@media (max-width: 768px) {
  .error-boundary {
    padding: 1rem;
  }
  
  .error-content {
    padding: 1.5rem;
  }
  
  .error-actions {
    flex-direction: column;
  }
  
  .error-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
